// Role choice list for the dev impersonation editor.
// Kept in its own module so the editor component stays under the file-size cap
// and so future sidebar entries can re-use the same labels.
//
// Tokens are organized into three groups (umbrella -> carved sub-roles ->
// platform tier) so a platform_dev can validate role-scoped panel rendering
// against any granularity without seeding real `user_roles` rows. Carved
// sub-roles per docs/design/role_taxonomy.md §4 (2026-06-15).

export type DevImpersonationRoleChoice = { id: string; label: string };

export const DEV_IMPERSONATION_ROLE_CHOICES: DevImpersonationRoleChoice[] = [
  // Umbrella tokens (the pre-2026-06-15 set).
  { id: "owner", label: "Org owner" },
  { id: "admin", label: "Org admin" },
  { id: "sales", label: "Sales" },
  { id: "trainer", label: "Trainer" },
  { id: "marketing", label: "Marketing" },
  { id: "ads_guidance_editor", label: "Ads guidance editor (add with Sales to mimic field bundle)" },
  { id: "dev", label: "Org dev (tenant technical — admin-class, not platform dev)" },
  { id: "manufacturing", label: "Manufacturing (desk umbrella — covers all carved mfg / production / logistics sub-roles)" },
  { id: "manufacturing_plant", label: "Manufacturing plant (floor parent — pair with phase:… tags)" },
  { id: "manufacturing_plant_supervisor", label: "Manufacturing plant supervisor (all phase transitions)" },
  { id: "manufacturing_plant_manager", label: "Manufacturing plant manager (Production Audits + Inventory count; no desk admin)" },
  { id: "manufacturing_purchasing", label: "Manufacturing purchasing (full Inventory + Models)" },
  { id: "mfg_qc", label: "Manufacturing QC (Builder Assistant and open QC inbox)" },
  { id: "mfg_final_qc", label: "Final QC (sign off a finished inspection, or send items back)" },
  { id: "manufacturing_repair_technician", label: "Manufacturing repair technician (Service materials usage only)" },
  { id: "phase:flooring", label: "Phase tag: flooring (with manufacturing_plant)" },
  { id: "phase:paint", label: "Phase tag: paint (with manufacturing_plant)" },
  // Carved sub-roles per role-taxonomy §4. Granting one of these WITHOUT
  // its umbrella is the new affordance — useful for validating the Order
  // Intake bucket move's role-scoped panel variants.
  { id: "sales_rep", label: "Sales rep (Sales-role salesIntake variant; no full Sales-bucket access)" },
  { id: "sales_manager", label: "Sales manager (rep-oversight tabs; no full admin)" },
  { id: "training_author", label: "Training author (scenario authoring; no full trainer)" },
  { id: "mfg_accepter", label: "Mfg accepter (Mfg-role salesIntake variant; no Manufacturing bucket)" },
  { id: "production_operator", label: "Production operator (floor kanban only; no desk Mfg)" },
  { id: "ops_reviewer", label: "Ops reviewer (Ops-role salesIntake variant + Ops Deals tab)" },
  { id: "logistics_coordinator", label: "Logistics coordinator (Routes + Carriers; canonical for `dispatcher`)" },
  { id: "driver", label: "Driver (driver-app surface only — host surface ships in driver-app slice)" },
  { id: "third_party_hauler_subcoordinator", label: "3rd-party hauler subcoordinator (scoped per-carrier view-only)" },
  // Platform tier.
  { id: "platform_admin", label: "Platform admin" },
  { id: "platform_dev", label: "Platform dev" },
  { id: "platform_trainer", label: "Platform trainer" },
];
